import { useState, useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../lib/supabase";
import { bankAccount } from "../../../mocks/finance";

interface EventLog {
  id: string;
  event_type: string;
  event_title: string;
  chosen_action: string;
  capital_change: number;
  reputation_change: number;
  created_at: string;
}

type FilterType = "전체" | "입금" | "출금";

const INITIAL_CAPITAL = 50000; // 초기 자본금 5,000만원

export default function BankAccount() {
  const { companyState, workspaceId } = useCompany();
  const { user } = useAuth();
  const [filter, setFilter] = useState<FilterType>("전체");
  const [eventLogs, setEventLogs] = useState<EventLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  const capital = companyState?.capital ?? 0;
  const capitalPct = Math.min(100, Math.round((capital / INITIAL_CAPITAL) * 100));
  const capitalChange = capital - INITIAL_CAPITAL;

  useEffect(() => {
    if (!workspaceId || !user?.id) { setLoadingLogs(false); return; }
    supabase
      .from("event_logs")
      .select("id, event_type, event_title, chosen_action, capital_change, reputation_change, created_at")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(30)
      .then(({ data }) => {
        if (data) setEventLogs(data as EventLog[]);
        setLoadingLogs(false);
      });
  }, [workspaceId, user?.id]);

  const allLogs: EventLog[] = eventLogs;
  const filtered = filter === "전체"
    ? allLogs
    : filter === "입금"
    ? allLogs.filter(l => l.capital_change > 0)
    : allLogs.filter(l => l.capital_change < 0);

  const totalIn = allLogs.filter(l => l.capital_change > 0).reduce((s, l) => s + l.capital_change, 0);
  const totalOut = allLogs.filter(l => l.capital_change < 0).reduce((s, l) => s + l.capital_change, 0);

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getEventTypeLabel = (type: string) => {
    if (type === "opportunity") return { label: "기회", color: "text-[#4B6CF7] bg-[#4B6CF7]/[0.08] border-[#4B6CF7]/20" };
    if (type === "crisis") return { label: "위기", color: "text-red-500 bg-red-50 border-red-100" };
    return { label: "이벤트", color: "text-[#888888] bg-[#F5F5F5] border-black/[0.09]" };
  };

  return (
    <div className="space-y-5">
      {/* ── 잔액 카드 ── */}
      <div className="bg-white border border-black/[0.08] rounded-xl overflow-hidden">
        <div className="p-5 border-b border-black/[0.07]">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-[#AAAAAA] text-xs mb-0.5">{bankAccount.bankName}</p>
              <p className="text-[#888888] font-mono text-sm">{bankAccount.accountNumber}</p>
            </div>
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-100 border border-emerald-200">
              <i className="ri-bank-card-line text-emerald-500 text-xl"></i>
            </div>
          </div>
          <p className="text-[#AAAAAA] text-xs mb-1">가상 자본금 잔액</p>
          <div className="flex items-end gap-3">
            <span className="text-[#111111] text-3xl font-bold tabular-nums">
              {capital.toLocaleString()}
            </span>
            <span className="text-[#888888] text-base mb-1">만원</span>
            <span className={`text-sm font-semibold mb-1 ml-auto ${capitalChange >= 0 ? "text-emerald-500" : "text-red-500"}`}>
              {capitalChange >= 0 ? "▲" : "▼"} {Math.abs(capitalChange).toLocaleString()}만원
            </span>
          </div>

          {/* 자본 활용률 바 */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-[10px] mb-1.5">
              <span className="text-[#AAAAAA]">초기 자본 대비 현재 잔액</span>
              <span className={`font-semibold tabular-nums ${capitalPct >= 100 ? "text-emerald-500" : capitalPct >= 60 ? "text-[#4B6CF7]" : capitalPct >= 30 ? "text-amber-500" : "text-red-500"}`}>
                {capitalPct}%
              </span>
            </div>
            <div className="w-full bg-black/[0.06] rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-700 ${capitalPct >= 100 ? "bg-emerald-400" : capitalPct >= 60 ? "bg-[#4B6CF7]" : capitalPct >= 30 ? "bg-amber-400" : "bg-red-400"}`}
                style={{ width: `${Math.min(100, capitalPct)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[#BBBBBB] text-[9px]">0만원</span>
              <span className="text-[#BBBBBB] text-[9px]">초기 {INITIAL_CAPITAL.toLocaleString()}만원</span>
            </div>
          </div>
        </div>

        {/* ── 요약 통계 ── */}
        <div className="grid grid-cols-3 divide-x divide-black/[0.07]">
          <div className="px-4 py-3 text-center">
            <p className="text-[#AAAAAA] text-[9px] uppercase tracking-widest mb-1">총 입금</p>
            <p className="text-emerald-500 text-sm font-bold tabular-nums">+{totalIn.toLocaleString()}만</p>
          </div>
          <div className="px-4 py-3 text-center">
            <p className="text-[#AAAAAA] text-[9px] uppercase tracking-widest mb-1">총 출금</p>
            <p className="text-red-400 text-sm font-bold tabular-nums">{totalOut.toLocaleString()}만</p>
          </div>
          <div className="px-4 py-3 text-center">
            <p className="text-[#AAAAAA] text-[9px] uppercase tracking-widest mb-1">이벤트 수</p>
            <p className="text-[#111111] text-sm font-bold tabular-nums">{allLogs.length}건</p>
          </div>
        </div>
      </div>

      {/* ── 이벤트 거래 내역 ── */}
      <div className="bg-white border border-black/[0.08] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-black/[0.07] flex items-center justify-between">
          <h3 className="text-[#111111] text-sm font-semibold">자본금 변동 내역</h3>
          <div className="flex items-center gap-1">
            {(["전체", "입금", "출금"] as FilterType[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filter === f
                    ? "bg-[#4B6CF7]/10 text-[#4B6CF7] border border-[#4B6CF7]/20"
                    : "text-[#888888] hover:text-[#555555] border border-transparent"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loadingLogs ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-black/[0.08] border-t-[#4B6CF7] rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <i className="ri-exchange-line text-3xl text-[#CCCCCC] mb-2"></i>
            <p className="text-[#CCCCCC] text-sm">이벤트 거래 내역이 없습니다</p>
            <p className="text-[#DDDDDD] text-xs mt-1">대시보드에서 이벤트에 대응하면 내역이 기록됩니다</p>
          </div>
        ) : (
          <div className="divide-y divide-black/[0.06]">
            {filtered.map((log) => {
              const typeMeta = getEventTypeLabel(log.event_type);
              const isIncome = log.capital_change > 0;
              return (
                <div key={log.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-black/[0.02] transition-colors">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 border ${
                    isIncome ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"
                  }`}>
                    <i className={`${isIncome ? "ri-arrow-down-line text-emerald-500" : "ri-arrow-up-line text-red-400"} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${typeMeta.color}`}>
                        {typeMeta.label}
                      </span>
                      <span className="text-[#333333] text-xs font-medium truncate">{log.event_title}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[#BBBBBB] text-[10px] truncate">{log.chosen_action}</span>
                    </div>
                    <span className="text-[#BBBBBB] text-[9px]">{formatTime(log.created_at)}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-bold tabular-nums ${isIncome ? "text-emerald-500" : "text-red-500"}`}>
                      {isIncome ? "+" : ""}{log.capital_change.toLocaleString()}만
                    </div>
                    {log.reputation_change !== 0 && (
                      <div className={`text-[10px] tabular-nums ${log.reputation_change > 0 ? "text-[#4B6CF7]" : "text-red-400"}`}>
                        명성 {log.reputation_change > 0 ? "+" : ""}{log.reputation_change}pt
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
